import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Stack,
  Typography,
  Tooltip,
  Divider,
  IconButton,
} from '@mui/material';
import ClientView from 'components/ClientView';
import Coordinates from 'components/Coordinates';
import ResponsiveKeyValueList from 'components/ResponsiveKeyValueList';
import Loading from 'core/components/Loading';
import {
  getGeolocationLink,
  getGeolocationWidgetLink,
  getOrderStateName,
} from 'core/utils/orderUtils';
import useResponsive from 'hooks/useResponsive';
import { isEmpty, isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useOrderPageStore } from './store/useOrderPageStore';
import type { FC, ReactNode } from 'react';

export interface OrderViewProps {
  actions?: ReactNode;
}

const OrderView: FC<OrderViewProps> = observer(({ actions }) => {
  const smLtScreen = useResponsive('down', 'sm');

  const orderPageStore = useOrderPageStore();

  const geolocationLink = useMemo(
    () =>
      !isNil(orderPageStore.order?.geolocation) && !isNil(orderPageStore.order)
        ? getGeolocationLink(orderPageStore.order.geolocation)
        : '',
    [orderPageStore.order]
  );

  const geolocationWidgetLink = useMemo(
    () =>
      !isNil(orderPageStore.order) && !isNil(orderPageStore.order?.geolocation)
        ? getGeolocationWidgetLink(orderPageStore.order.geolocation)
        : '',
    [orderPageStore.order]
  );

  if (isNil(orderPageStore.order)) {
    return <Loading />;
  }

  return (
    <>
      <Stack gap={2}>
        {!isEmpty(orderPageStore.order.warningComment) && (
          <Alert severity='warning'>{orderPageStore.order.warningComment}</Alert>
        )}

        {!isNil(actions) && (
          <Stack direction={'row'} gap={2}>
            {actions}
          </Stack>
        )}

        <Box>
          <Divider sx={{ width: '100%' }} />
          <ResponsiveKeyValueList
            rows={[
              {
                label: 'Текущее состояние',
                value: !isNil(orderPageStore.order.state)
                  ? getOrderStateName(orderPageStore.order.state)
                  : '',
              },
              {
                label: 'Адрес',
                value: orderPageStore.order.address ?? '',
              },
              {
                label: 'Координаты',
                value: !isNil(orderPageStore.order.geolocation) && (
                  <>
                    <Coordinates data={orderPageStore.order.geolocation} />
                    {smLtScreen && (
                      <IconButton
                        aria-label='Open map in new tab'
                        target='_blank'
                        size='small'
                        href={geolocationLink}>
                        <OpenInNewIcon fontSize='inherit' />
                      </IconButton>
                    )}
                  </>
                ),
              },
              {
                label: 'Комментарий',
                value: orderPageStore.order.remark ?? '',
              },
            ]}
          />
        </Box>
      </Stack>
      {!isNil(orderPageStore.order.client) && (
        <Box>
          <Stack direction={'row'} alignItems={'center'} mt={3} px={2}>
            <Typography variant='h5' component='h2'>
              {orderPageStore.order.client.fullName}
            </Typography>

            <Tooltip title='Открыть страницу клиента в новой вкладке'>
              <IconButton
                size='small'
                aria-label='Go to client page'
                component={RouterLink}
                to={`/clients/${orderPageStore.order.client?.id ?? ''}`}
                target='_blank'>
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ mt: 0.5, maxWidth: 800 }} />

          <ClientView client={orderPageStore.order.client} />
        </Box>
      )}

      {!isNil(orderPageStore.order.geolocation) && !smLtScreen && (
        <iframe
          style={{ border: 'none', width: '100%', height: '400px' }}
          title='orderGeolocationCoordinatesMap'
          src={geolocationWidgetLink}
        />
      )}
    </>
  );
});

export default OrderView;
